import { z } from "zod"
import { MarketName } from "@/constants/marketName"
import { ModelAddressSchema } from "@/features/subscription/schemas/modelAddressSchema"
import { getPaginatedNodesSchema } from "@/schemas/getPaginatedNodesSchema"
import { MAX_SUBSCRIBED_POWER, MIN_SUBSCRIBED_POWER } from "../constants/subscribedPower"
import { PcePoweredStatus, PrmPoweredStatus } from "../types/poweredStatus"
import { ElectricityMeterPointKindSchema } from "./electricityMeterPointKindSchema"
import { OffPeakValuesSchema } from "./offPeakValuesSchema"

// MeterPoint Schemas
const MeterPointSchema = z.object({
    id: z.string(),
    isSmartMeter: z.boolean(),
    address: ModelAddressSchema,
})

const ElectricityMeterPointSchema = MeterPointSchema.extend({
    providerCalendar: z
        .object({
            id: z.string().nullable(),
            name: z.string().nullable(),
        })
        .nullable()
        .optional(),
    meterKind: ElectricityMeterPointKindSchema.nullable(),
    offPeakLabel: z.string().nullable(),
    offPeakValues: OffPeakValuesSchema,
    subscribedMaxPower: z.coerce
        .number()
        .min(MIN_SUBSCRIBED_POWER)
        .max(MAX_SUBSCRIBED_POWER)
        .nullable(),
    poweredStatus: z.enum(PrmPoweredStatus).nullable(),
    serviceLevel: z.number().nullable(),
    isSensitive: z.boolean().nullable(),
    circuitBreakerIntensity: z.string().nullable(),
})

const GasMeterPointSchema = MeterPointSchema.extend({
    priceLevel: z.number().nullable(),
    poweredStatus: z.enum(PcePoweredStatus).nullable(),
})

// Eligibility Result Schemas
const EligibilityErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.string(),
})
const BaseEligibilityResultSchema = z.object({
    isEligible: z.boolean(),
    errors: z.array(EligibilityErrorSchema).nullable().optional(),
    measurementsHasBeenFetched: z.boolean().nullable(),
})

export const ElectricityEligibilityResultSchema = BaseEligibilityResultSchema.extend({
    supplyPoint: z.object({
        id: z.string(),
        marketName: z.literal(MarketName.FRA_ELECTRICITY),
        meterPoint: ElectricityMeterPointSchema,
    }),
})

export const GasEligibilityResultSchema = BaseEligibilityResultSchema.extend({
    supplyPoint: z.object({
        id: z.string(),
        marketName: z.literal(MarketName.FRA_GAS),
        meterPoint: GasMeterPointSchema,
    }),
})

const CheckMeterPointsEligibilitySchema = z.union([
    ElectricityEligibilityResultSchema,
    GasEligibilityResultSchema,
])

export const CheckMeterPointEligibilitySchema = z.object({
    checkMeterPointsEligibility: z.object({
        eligibilityResults: getPaginatedNodesSchema(CheckMeterPointsEligibilitySchema, 1),
    }),
})
